import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { GridResponse, GridCell } from '../types'
import {
  ViewMode,
  ProductsCellRef,
  AISLE_CHIP_W,
  GRID_COL_HEADER_TEXT,
} from '../config/constants'
import { isAxisValue, numSort } from '../config/utils'
import { layoutGridStyles as styles } from '../styles/LayoutGrid.styles'
import { axisLabelSx, gridStyles } from '../styles/shared.styles'
import { MapCell } from './MapCell'
import { CellSummaryPopper } from './CellSummaryPopper'
import { useCellSummary } from '../hooks/useCellSummary'
import { useAvailableHeight } from '../hooks/useAvailableHeight'


export const LayoutGrid = React.memo(function LayoutGrid({
  gridData,
  mode,
  activeAisle,
  brandColors,
  zoneColors,
  highlightBrand = null,
  onAisleClick,
  onCellClick,
}: {
  gridData: GridResponse
  mode: ViewMode
  activeAisle: string | null
  brandColors: Map<string, string>
  zoneColors: Map<string, string>
  highlightBrand?: string | null
  onAisleClick: (aisle: string) => void
  onCellClick: (cell: ProductsCellRef) => void
}) {
  const { containerRef, open, setOpen, close } = useCellSummary<HTMLDivElement>()
  const availableHeight = useAvailableHeight(containerRef)
  const hasPannedRef = useRef(false)
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 })
  const [isDragging, setIsDragging] = useState(false)

  const sortedAisles = useMemo(
    () => gridData.aisles.filter(isAxisValue).sort(numSort),
    [gridData.aisles]
  )
  const sortedColumns = useMemo(
    () => gridData.columns.filter(isAxisValue).sort(numSort),
    [gridData.columns]
  )
  const cellSize = Math.max(32, Math.min(54, Math.floor(1100 / Math.max(sortedColumns.length, 1))))

  const cellMap = useMemo(() => {
    const map = new Map<string, GridCell>()
    gridData.cells.forEach((c) => map.set(`${c.aisle}-${c.column}`, c))
    return map
  }, [gridData.cells])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return
    hasPannedRef.current = false
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop,
    }
    setIsDragging(true)

    const onMove = (ev: MouseEvent) => {
      if (!containerRef.current) return
      const dx = ev.clientX - panStartRef.current.x
      const dy = ev.clientY - panStartRef.current.y
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        if (!hasPannedRef.current) close()
        hasPannedRef.current = true
        containerRef.current.scrollLeft = panStartRef.current.scrollLeft - dx
        containerRef.current.scrollTop = panStartRef.current.scrollTop - dy
      }
    }

    const onUp = () => {
      setIsDragging(false)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [])

  const handleMapClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasPannedRef.current) e.stopPropagation()
  }, [])

  /** Click simple: muestra el resumen de la celda. */
  const handleCellClick = useCallback(
    (cellKey: string, anchorEl: HTMLElement) => setOpen({ key: cellKey, anchorEl }),
    [setOpen]
  )

  /**
   * Doble click: abre el detalle. El primer click del gesto ya abrió el
   * resumen, así que se cierra antes de montar el modal.
   */
  const handleCellDoubleClick = useCallback(
    (cellKey: string) => {
      const cell = cellMap.get(cellKey)
      if (!cell?.isConfigured) return

      close()
      onCellClick({
        aisle: cell.aisle,
        column: cell.column,
        totalStock: cell.totalStock,
        maxCapacity: cell.maxCapacity,
        occupancyPct: cell.occupancyPct,
      })
    },
    [cellMap, close, onCellClick]
  )

  const gridTemplateColumns = `${AISLE_CHIP_W}px repeat(${sortedColumns.length}, ${cellSize}px)`

  return (
    <Box sx={styles.root}>
      <Box sx={axisLabelSx}>
        PASILLO
      </Box>
      <Box sx={gridStyles.axisColumn}>
        <Typography
          variant='caption'
          fontWeight='bold'
          color='text.secondary'
          sx={gridStyles.columnAxisLabel(AISLE_CHIP_W)}
        >
          COLUMNA
        </Typography>

        <Box
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onClickCapture={handleMapClickCapture}
          onDoubleClickCapture={handleMapClickCapture}
          sx={styles.scrollArea(isDragging, availableHeight)}
        >
          <Box sx={styles.grid(gridTemplateColumns)}>
            {/* Esquina sup-izq */}
            <Box sx={gridStyles.cornerCell} />

            {/* Cabeceras de columna */}
            {sortedColumns.map((col) => (
              <Box key={col} sx={gridStyles.columnHeader}>
                <Typography variant='caption' fontWeight={600} fontSize='0.65rem' color={GRID_COL_HEADER_TEXT}>
                  {col}
                </Typography>
              </Box>
            ))}

            {/* Filas de pasillo */}
            {sortedAisles.map((aisle) => {
              const isActive = aisle === activeAisle
              return (
                <Fragment key={aisle}>
                  <Tooltip
                    title={`Ver detalle del pasillo ${aisle}`}
                    placement='right'
                    slotProps={{ popper: { sx: styles.aisleTooltipPopper } }}
                  >
                    <Box
                      onClick={() => onAisleClick(aisle)}
                      sx={styles.aisleChip(isActive, cellSize)}
                    >
                      <Typography variant='caption' fontWeight='bold' fontSize='0.7rem' sx={styles.aisleChipText}>
                        {aisle}
                      </Typography>
                    </Box>
                  </Tooltip>

                  {sortedColumns.map((col) => {
                    const cell = cellMap.get(`${aisle}-${col}`)
                    if (!cell) {
                      return <Box key={col} sx={gridStyles.unconfiguredCell(cellSize)} />
                    }
                    const cellKey = `${aisle}-${col}`
                    return (
                      <MapCell
                        key={col}
                        cell={cell}
                        size={cellSize}
                        mode={mode}
                        brandColors={brandColors}
                        zoneColors={zoneColors}
                        highlightBrand={highlightBrand}
                        cellKey={cellKey}
                        isSummaryOpen={open?.key === cellKey}
                        onClick={handleCellClick}
                        onDoubleClick={handleCellDoubleClick}
                      />
                    )
                  })}
                </Fragment>
              )
            })}
          </Box>
        </Box>
      </Box>
      <CellSummaryPopper
        openCell={open}
        cell={open ? cellMap.get(open.key) : undefined}
        rowLabel='Pasillo'
        brandColors={brandColors}
        zoneColors={zoneColors}
        showDetailHint={open ? Boolean(cellMap.get(open.key)?.isConfigured) : false}
      />
    </Box>
  )
})
